import { decodeString } from "@findemdev/findem-helpers";
import { notification } from "@findemdev/maui";
import { useCallback, useEffect, useState } from "react";
import {
  useCreateExecProjectMutation,
  useDeleteExecProjectMutation,
  useUpdateExecProjectMutation,
} from "../../../services/execProjects";
import {
  ExecProjectErrorCodes,
  ICreateExecProjectRequest,
  IExecProject,
  IUpdateExecProjectRequest,
} from "../../../types/agent-pipelines";
import { DEFAULT_FORM } from "../constants";
import type { RaasProjectPlatformType } from "../platform";
import {
  RaasProjectClientMode,
  type IProjectFormData,
  type ProjectFormFieldKey,
} from "../types";
import { getSafeErrorMessage, normalizeDomain } from "../utils";

const getPermissionErrorCode = ({ error }: { error: unknown }): string | undefined =>
  typeof error === "object" && error !== null
    ? (error as { data?: { error_code?: string } }).data?.error_code
    : undefined;

const getDefaultFormData = ({
  defaultClientLabel,
}: {
  defaultClientLabel?: string;
}): IProjectFormData => ({
  ...DEFAULT_FORM,
  client_name: decodeString(defaultClientLabel || ""),
});

export const useRaasProjectActions = ({
  currentUserAccountId,
  defaultClientLabel,
  isProjectPlatformResolutionPending,
  joid,
  onProjectCreated,
  platformType,
  projectFormFieldKeys,
}: {
  currentUserAccountId?: string;
  defaultClientLabel?: string;
  isProjectPlatformResolutionPending: boolean;
  joid?: string;
  onProjectCreated?: ({ project }: { project: IExecProject }) => void;
  platformType: RaasProjectPlatformType;
  projectFormFieldKeys: readonly ProjectFormFieldKey[];
}) => {
  const hasClientField = projectFormFieldKeys.includes("client");
  const hasAssignedToField = projectFormFieldKeys.includes("acting_user_id");
  const defaultFormClientLabel = hasClientField ? undefined : defaultClientLabel;
  const hiddenClientName = decodeString(defaultClientLabel || "").trim();
  const [isProjectModalVisible, setIsProjectModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<IExecProject | null>(
    null,
  );
  const [clientMode, setClientMode] = useState<RaasProjectClientMode>(
    RaasProjectClientMode.Enterprise,
  );
  const [formData, setFormData] = useState<IProjectFormData>(() =>
    getDefaultFormData({ defaultClientLabel: defaultFormClientLabel }),
  );
  const [projectToDelete, setProjectToDelete] =
    useState<IExecProject | null>(null);
  const [createProject, { isLoading: isCreating }] =
    useCreateExecProjectMutation();
  const [updateProject, { isLoading: isUpdating }] =
    useUpdateExecProjectMutation();
  const [deleteProject, { isLoading: isDeleting }] =
    useDeleteExecProjectMutation();

  const selectedClientCompanyAccountId = hasClientField
    ? formData.client_company_account_id.trim()
    : currentUserAccountId?.trim() || "";
  // Project search rows can omit the account id even for newly created
  // projects. Preserve an unchanged, persisted client identity during edit.
  const hasUnchangedClientWithoutAccountId = Boolean(
    editingProject &&
      !editingProject.client_company_account_id?.trim() &&
      Boolean(formData.client_company_id.trim()) &&
      formData.client_name.trim() ===
        decodeString(editingProject.client_name || "").trim() &&
      formData.client_company_id.trim() ===
        (editingProject.client_company_id || "").trim(),
  );
  const hasRequiredClient = hasClientField
    ? Boolean(
        formData.client_name.trim() &&
          (selectedClientCompanyAccountId ||
            hasUnchangedClientWithoutAccountId),
      )
    : Boolean(hiddenClientName && selectedClientCompanyAccountId);
  const requiresHiringCompany = Boolean(
    hasClientField &&
      !editingProject &&
      clientMode === RaasProjectClientMode.Agency,
  );
  const hasRequiredHiringCompany =
    !requiresHiringCompany ||
    Boolean(
      formData.hiring_company_name.trim() && formData.hiring_company_id.trim(),
    );
  const hasRequiredProjectFields = Boolean(
    formData.project_name.trim() &&
      formData.job_title.trim() &&
      hasRequiredClient &&
      hasRequiredHiringCompany,
  );
  const canSubmitProject =
    hasRequiredProjectFields && !isProjectPlatformResolutionPending;

  useEffect(() => {
    if (hasClientField || !isProjectModalVisible) return;

    setFormData((previousFormData) =>
      previousFormData.client_name === hiddenClientName
        ? previousFormData
        : { ...previousFormData, client_name: hiddenClientName },
    );
  }, [hasClientField, hiddenClientName, isProjectModalVisible]);

  const openCreateModal = useCallback(() => {
    setEditingProject(null);
    setClientMode(RaasProjectClientMode.Enterprise);
    setFormData(
      getDefaultFormData({ defaultClientLabel: defaultFormClientLabel }),
    );
    setIsProjectModalVisible(true);
  }, [defaultFormClientLabel]);

  const openEditModal = useCallback(
    ({ project }: { project: IExecProject }) => {
      setEditingProject(project);
      setFormData({
        project_name: decodeString(project.project_name),
        job_title: project.job_title || "",
        acting_user_id: project.acting_user_id || "",
        client_name: decodeString(!hasClientField ? (defaultFormClientLabel || "") : (project.client_name || "")),
        client_company_id: project.client_company_id || "",
        client_company_account_id: project.client_company_account_id || "",
        client_domain: project.client_domain || "",
        hiring_company_name: project.hiring_company_name || "",
        hiring_company_id: project.hiring_company_id || "",
      });
      setIsProjectModalVisible(true);
    },
    [defaultFormClientLabel, hasClientField],
  );

  const closeProjectModal = useCallback(() => {
    setIsProjectModalVisible(false);
    setEditingProject(null);
    setClientMode(RaasProjectClientMode.Enterprise);
    setFormData(
      getDefaultFormData({ defaultClientLabel: defaultFormClientLabel }),
    );
  }, [defaultFormClientLabel]);

  const handleClientModeChange = useCallback(
    (nextClientMode: RaasProjectClientMode) => {
      if (!hasClientField || editingProject || nextClientMode === clientMode) {
        return;
      }

      setClientMode(nextClientMode);
      setFormData((previousFormData) => ({
        ...previousFormData,
        client_name: "",
        client_company_id: "",
        client_company_account_id: "",
        client_domain: "",
        hiring_company_name: "",
        hiring_company_id: "",
      }));
    },
    [clientMode, editingProject, hasClientField],
  );

  const handleSubmit = useCallback(async () => {
    if (!canSubmitProject) return;

    const effectiveClientName = hasClientField
      ? formData.client_name.trim()
      : hiddenClientName;
    const basePayload = {
      project_name: formData.project_name.trim(),
      display_name: formData.project_name.trim(),
      job_title: formData.job_title.trim(),
      client_name: effectiveClientName,
      client_company_id: formData.client_company_id.trim() || `company_${Date.now()}`,
      client_domain: normalizeDomain({
        companyName: effectiveClientName,
        domain: formData.client_domain,
      }),
      platform_type: platformType,
      joid,
    };
    const hiringCompanyPayload = {
      hiring_company_name: formData.hiring_company_name.trim(),
      hiring_company_id: formData.hiring_company_id.trim(),
    };
    const canUseHiringCompany = Boolean(
      !hasClientField ||
        editingProject ||
        clientMode === RaasProjectClientMode.Agency,
    );
    const shouldIncludeHiringCompany = Boolean(
      canUseHiringCompany &&
        hiringCompanyPayload.hiring_company_id &&
        hiringCompanyPayload.hiring_company_name,
    );
    const hiringCompanyUpdatePayload = shouldIncludeHiringCompany
      ? hiringCompanyPayload
      : { hiring_company_id: "", hiring_company_name: "" };

    try {
      let projectToOpen: IExecProject | undefined;

      if (editingProject) {
        const updatePayload: IUpdateExecProjectRequest = {
          ref: editingProject.ref,
          ...basePayload,
          ...(selectedClientCompanyAccountId
            ? { client_company_account_id: selectedClientCompanyAccountId }
            : {}),
          ...hiringCompanyUpdatePayload,
          ...(hasAssignedToField
            ? { acting_user_id: formData.acting_user_id.trim() || null }
            : {}),
        };

        await updateProject(updatePayload).unwrap();

        notification.success({
          message: "Project Updated",
          description: `Successfully updated ${decodeString(basePayload.project_name)}.`,
        });
      } else {
        const createPayload: ICreateExecProjectRequest = {
          ...basePayload,
          client_company_account_id: selectedClientCompanyAccountId,
          ...(shouldIncludeHiringCompany ? hiringCompanyPayload : {}),
        };
        const createdProject = await createProject(createPayload).unwrap();
        projectToOpen = createdProject;

        notification.success({
          message: "Project Created",
          description: `Successfully created ${decodeString(basePayload.project_name)}.`,
        });
      }

      closeProjectModal();
      if (projectToOpen) {
        onProjectCreated?.({ project: projectToOpen });
      }
    } catch (error) {
      const errorCode = getPermissionErrorCode({ error });

      notification.error({
        message:
          errorCode === ExecProjectErrorCodes.FORBIDDEN
            ? "Permission Denied"
            : editingProject
              ? "Failed to Update Project"
              : "Failed to Create Project",
        description:
          errorCode === ExecProjectErrorCodes.FORBIDDEN
            ? "You do not have permission to perform this action."
            : getSafeErrorMessage({ error }),
      });
    }
  }, [
    closeProjectModal,
    createProject,
    currentUserAccountId,
    editingProject,
    formData,
    canSubmitProject,
    clientMode,
    hasAssignedToField,
    hasClientField,
    hiddenClientName,
    joid,
    onProjectCreated,
    platformType,
    updateProject,
  ]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!projectToDelete) return;

    try {
      await deleteProject({ ref: projectToDelete.ref, joid }).unwrap();
      notification.success({
        message: "Project Deleted",
        description: `Successfully deleted ${decodeString(projectToDelete.project_name)}.`,
      });
      setProjectToDelete(null);
    } catch (error) {
      notification.error({
        message: "Failed to Delete Project",
        description: getSafeErrorMessage({ error }),
      });
    }
  }, [deleteProject, joid, projectToDelete]);

  const closeDeleteModal = useCallback(() => {
    setProjectToDelete(null);
  }, []);

  const requestDeleteProject = useCallback(
    ({ project }: { project: IExecProject }) => {
      setProjectToDelete(project);
    },
    [],
  );

  const projectToDeleteName = projectToDelete
    ? decodeString(projectToDelete.project_name)
    : "this project";

  return {
    clientMode,
    closeDeleteModal,
    closeProjectModal,
    editingProject,
    formData,
    handleClientModeChange,
    handleDeleteConfirm,
    handleSubmit,
    isCreating,
    isDeleting,
    isDeleteModalVisible: Boolean(projectToDelete),
    canSubmitProject,
    isProjectModalVisible,
    isUpdating,
    openCreateModal,
    openEditModal,
    projectToDeleteName,
    requestDeleteProject,
    setFormData,
  };
};
